import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, parseJsonSafe } from '../../utils/api';
import { getCustomerToken, clearCustomerToken } from '../customerAuth';

const CustomerContext = createContext(null);

export const useCustomer = () => useContext(CustomerContext);

export const CustomerProvider = ({ children }) => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [domains, setDomains] = useState([]);
  const [hostings, setHostings] = useState([]);
  const [projects, setProjects] = useState([]);
  const [timePacks, setTimePacks] = useState([]);
  const [services, setServices] = useState([]);
  const [companyLogo, setCompanyLogo] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${getCustomerToken()}` }), []);

  const logout = useCallback(() => {
    clearCustomerToken();
    navigate('/cliente/login');
  }, [navigate]);

  const refreshData = useCallback(async () => {
    const token = getCustomerToken();
    if (!token) { logout(); return; }

    setLoading(true);
    setError('');
    try {
      // allSettled: un rechazo de fetch (red/CORS/DNS/offline) en un recurso no debe
      // tumbar el portal entero. Cada resultado se trata por separado.
      const settled = await Promise.allSettled([
        apiFetch('/api/v1/software/customer/portal/me', { headers: authHeaders() }),
        apiFetch('/api/v1/software/customer/portal/invoices', { headers: authHeaders() }),
        apiFetch('/api/v1/software/customer/portal/budgets', { headers: authHeaders() }),
        apiFetch('/api/v1/software/customer/portal/domains', { headers: authHeaders() }),
        apiFetch('/api/v1/software/customer/portal/hostings', { headers: authHeaders() }),
        apiFetch('/api/v1/software/customer/portal/projects', { headers: authHeaders() }),
        apiFetch('/api/v1/software/customer/portal/time-packs', { headers: authHeaders() }),
        apiFetch('/api/v1/software/customer/portal/services', { headers: authHeaders() })
      ]);
      const [meS, invS, budS, domS, hosS, proS, tpS, svcS] = settled;

      // El 401 solo puede detectarse en respuestas obtenidas (fulfilled); los rechazos no tienen .status
      if (settled.some(r => r.status === 'fulfilled' && r.value.status === 401)) {
        logout();
        return;
      }

      // El portal SÍ depende de /me: si su fetch se rechazó (error de red), es un error de portal
      if (meS.status === 'rejected') {
        throw (meS.reason instanceof Error ? meS.reason : new Error('No se pudo cargar el portal'));
      }
      const meRes = meS.value;
      const meBody = await parseJsonSafe(meRes);
      if (!meRes.ok) throw new Error(meBody.message || 'No se pudo cargar el portal');

      setCustomer(meBody.customer || {});
      setCompanyLogo(meBody.company_logo || '');
      setLicenses(meBody.licenses || []);

      // Recursos secundarios: parsear JSON solo de los fulfilled+ok; el resto queda en []
      const parseSettled = async (s) => (s.status === 'fulfilled' && s.value.ok ? await parseJsonSafe(s.value) : null);
      const [invBody, budBody, domBody, hosBody, proBody, tpBody, svcBody] = await Promise.all([
        parseSettled(invS), parseSettled(budS), parseSettled(domS),
        parseSettled(hosS), parseSettled(proS), parseSettled(tpS), parseSettled(svcS)
      ]);

      setInvoices(Array.isArray(invBody) ? invBody : []);
      setBudgets(Array.isArray(budBody) ? budBody : []);
      setDomains(Array.isArray(domBody) ? domBody : []);
      setHostings(Array.isArray(hosBody) ? hosBody : []);
      setProjects(Array.isArray(proBody) ? proBody : []);
      setTimePacks(Array.isArray(tpBody) ? tpBody : []);
      setServices(Array.isArray(svcBody) ? svcBody : []);
      // Avisar si algún recurso (no /me, no 401) falló: evita mostrar "sin datos" engañoso.
      // Cuenta tanto rechazos de fetch (red) como respuestas HTTP no-ok.
      const fallidos = [invS, budS, domS, hosS, proS, tpS, svcS].filter(
        (s) => s.status === 'rejected' || (s.value.status !== 401 && !s.value.ok)
      );
      if (fallidos.length) setError('Algunos datos no se pudieron cargar. Pulsa "Reintentar".');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  useEffect(() => { refreshData(); }, [refreshData]);

  return (
    <CustomerContext.Provider value={{
      customer, licenses, invoices, budgets, domains, hostings, projects, timePacks, services, companyLogo,
      loading, error, logout, refreshData, authHeaders
    }}>
      {children}
    </CustomerContext.Provider>
  );
};
